import { useMemo, useState } from 'react';
import { useHistory } from 'react-router';
import { SwapCalls } from '@mui/icons-material';
import { Stack } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { CampaignStatus, URLPaths } from 'models/enums';
import useCampaigns from 'hooks/queries/useCampaigns';
import useContacts from 'hooks/queries/useContacts';
import useCurationsQueryParams from 'hooks/useCurationsQueryParams';
import Button from 'domains/core/components/Button';
import EmptyStateLayout from 'domains/core/components/EmptyStateLayout';
import PageLayout from 'domains/core/components/PageLayout';
import SearchField from 'domains/core/components/SearchField';
import ContactsUploadDialog from 'domains/contacts/components/ContactsUploadDialog';
import FileUploadButton from 'domains/contacts/components/FileUploadButton';
import useContactsFileUploadProps from 'domains/contacts/hooks/useContactsFileUploadProps';
import CampaignsTable from './CampaignsTable';
import MultiSelectDropdown, { MultiSelectOption } from 'domains/core/components/MultiSelectDropdown';

const toLabel = (status: string): string =>
    status
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\bab\b/g, 'A/B')
        .replace(/\b\w/g, (c) => c.toUpperCase());

const abTestStatuses = new Set<CampaignStatus>([
    CampaignStatus.SENDING_AB_TEST,
    CampaignStatus.PENDING_WINNER_SELECTION,
    CampaignStatus.WINNER_DECLARED,
    CampaignStatus.WINNER_SCHEDULED,
    CampaignStatus.SENDING_WINNER,
    CampaignStatus.WINNER_SENT,
    CampaignStatus.WINNER_FAILED,
    CampaignStatus.WINNER_STOPPED,
]);

const allCampaignStatusOptions: MultiSelectOption<CampaignStatus>[] = Object.values(CampaignStatus).map((status) => ({
    value: status,
    label: toLabel(status),
}));

export const CampaignsPage = () => {
    const history = useHistory();
    const [searchTerm, setSearchTerm] = useState('');
    const { statuses, changeViewParams } = useCurationsQueryParams();
    const { aBTesting: hasABTesting, statusFilterCuration: hasStatusFilterCuration } = useFlags();

    const campaignStatusOptions = useMemo(
        () =>
            hasABTesting
                ? allCampaignStatusOptions
                : allCampaignStatusOptions.filter((o) => !abTestStatuses.has(o.value)),
        [hasABTesting]
    );

    const {
        data: campaigns,
        isRefetching: isCampaignsRefetching,
        isSuccess: isCampaignsSuccess,
        refetch: refetchCampaigns,
    } = useCampaigns({
        searchTerm,
        statuses,
        reactQueryOptions: {
            refetchInterval: 30000,
        },
    });
    const { data: contacts, isSuccess: isContactsSuccess } = useContacts({
        options: { limit: '1' },
    });

    const fileUploadProps = useContactsFileUploadProps();

    const isContactsEmpty = !contacts?.length;

    const handleStatusFilterApply = (newStatuses: CampaignStatus[]) => {
        changeViewParams({ newStatuses });
    };

    if (isContactsSuccess && isContactsEmpty) {
        return (
            <EmptyStateLayout
                caption="curations"
                header="create authentic connections"
                guidingText="Once your contacts are uploaded, build your first Curation to begin communicating with your contacts."
            >
                <ContactsUploadDialog {...fileUploadProps} />
                <FileUploadButton {...fileUploadProps} />
            </EmptyStateLayout>
        );
    }

    return (
        <PageLayout
            header="curations"
            headerAction={
                <Button
                    endIcon={<SwapCalls />}
                    onClick={() => history.push(URLPaths.CURATIONS_LANDING)}
                    variant="outlined"
                >
                    Create
                </Button>
            }
            headerIcon={<SwapCalls fontSize="inherit" />}
            headerOthers={
                <Stack direction="row" spacing={4} alignItems="center">
                    {hasStatusFilterCuration && (
                        <MultiSelectDropdown
                            options={campaignStatusOptions}
                            selectedValues={statuses}
                            onApply={handleStatusFilterApply}
                            label="Status"
                            testIdPrefix="campaign-status-filter"
                            menuId="campaign-status-filter-menu"
                            maxHeight="400px"
                        />
                    )}
                    <SearchField
                        placeholder="Search curations..."
                        value={searchTerm}
                        onChange={setSearchTerm}
                        dataTestId="curations-search-input"
                    />
                </Stack>
            }
        >
            {isCampaignsSuccess && (
                <CampaignsTable
                    campaigns={campaigns}
                    isRefetching={isCampaignsRefetching}
                    searchTerm={searchTerm}
                    refetch={refetchCampaigns}
                />
            )}
        </PageLayout>
    );
};

export default CampaignsPage;
